import { useMemo, useRef, useState } from 'react'
import Avatar from '../components/Avatar'
import { useComptes, usePersonnes, useRecapRepas, useRepas, useSetPresenceRepas } from '../api/hooks'
import { useHorizontalScrollSync } from '../components/useHorizontalScrollSync'
import type { Personne as PersonneT } from '../api/types'

const fmtEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

export default function RepasPage() {
  const repasQ = useRepas()
  const personnesQ = usePersonnes()
  const comptesQ = useComptes()
  const recapQ = useRecapRepas()
  const mut = useSetPresenceRepas()
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set())

  // === Sticky thead sur mobile : 2 tables (header + body), scroll horizontal synchronisé ===
  const headerScrollRef = useRef<HTMLDivElement | null>(null)
  const bodyScrollRef = useRef<HTMLDivElement | null>(null)
  useHorizontalScrollSync(bodyScrollRef, [headerScrollRef])

  const presenceMap = useMemo(() => {
    const m = new Map<string, boolean>()
    repasQ.data?.presences.forEach(p => m.set(`${p.personne_id}-${p.repas_id}`, true))
    return m
  }, [repasQ.data])

  const lignesParCompte = useMemo(() => {
    const map = new Map<number, { compte_nom: string; rang: number; personnes: PersonneT[] }>()
    comptesQ.data?.forEach(c => map.set(c.id, { compte_nom: c.nom, rang: c.rang, personnes: [] }))
    personnesQ.data?.forEach(p => {
      const entry = map.get(p.compte_id)
      if (entry && p.actif) entry.personnes.push(p)
    })
    return Array.from(map.entries())
      .filter(([, v]) => v.personnes.length > 0)
      .sort((a, b) => a[1].rang - b[1].rang || a[1].compte_nom.localeCompare(b[1].compte_nom))
  }, [comptesQ.data, personnesQ.data])

  if (!repasQ.data || !personnesQ.data || !comptesQ.data) {
    return <p className="text-ocean-700 italic">Chargement…</p>
  }

  const repas = repasQ.data.repas
  const montantParPers = new Map(recapQ.data?.personnes.map(p => [p.personne_id, p.montant_du]) ?? [])
  const nbParPers = new Map(recapQ.data?.personnes.map(p => [p.personne_id, p.nb_repas]) ?? [])
  const totalParCompte = new Map(recapQ.data?.comptes.map(c => [c.compte_id, c]) ?? [])

  const repasParJour = repas.reduce<Record<string, { M?: number; S?: number }>>((acc, r) => {
    acc[r.date] = { ...acc[r.date], [r.type]: r.id }
    return acc
  }, {})
  const jours = Object.keys(repasParJour).sort()

  const toggleFamille = (cid: number) => {
    setCollapsed(s => {
      const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n
    })
  }
  const allCollapsed = collapsed.size === lignesParCompte.length
  const setAll = (col: boolean) =>
    setCollapsed(col ? new Set(lignesParCompte.map(([cid]) => cid)) : new Set())

  return (
    <div className="space-y-4">
      <section className="card p-4 sm:p-5">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-ocean-900">
              🍽️ Repas pris · {jours.length} jours
            </h2>
            <p className="text-xs sm:text-sm text-ocean-700 mt-0.5">
              Cochez M (Midi) et S (Soir) pour chaque jour. Les bébés ne sont pas comptés.
            </p>
            <p className="text-[11px] sm:text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 mt-1.5 leading-snug max-w-xl">
              ✨ <strong>Cochage automatique</strong> : chaque nuitée confirmée dans l'onglet
              🌙 Nuitées coche automatiquement le <strong>midi et le soir du jour d'arrivée</strong> et
              le <strong>midi du lendemain</strong>. Tu peux <strong>corriger librement</strong> ici —
              tes corrections manuelles sont conservées et jamais écrasées.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAll(!allCollapsed)}
              className="btn-ghost btn-sm border border-ocean-200 hover:border-ocean-400"
            >
              {allCollapsed ? '▸ Tout déplier' : '▾ Tout replier'}
            </button>
            <div className="flex gap-2 text-xs">
              <Pill label="Dépenses" value={fmtEur(recapQ.data?.total_depenses ?? 0)} />
              <Pill label="Repas pris" value={`${recapQ.data?.nb_repas_total ?? 0}`} />
              <Pill label="Prix / repas" value={recapQ.data?.prix_par_repas ? fmtEur(recapQ.data.prix_par_repas) : '—'} />
            </div>
          </div>
        </div>
      </section>

      <div className="card">
        {/* En-tête sticky : table séparée, scroll horizontal sync avec le body via JS */}
        <div
          ref={headerScrollRef}
          className="sticky top-0 sm:top-[49px] z-30 overflow-x-hidden bg-ocean-50 shadow-[0_2px_4px_rgba(15,76,110,0.08)] rounded-t-2xl"
        >
          <table className="text-xs sm:text-sm w-max lg:w-full lg:table-fixed">
            <colgroup>
              <col className="w-[170px]" />
              {jours.flatMap(j => [
                <col key={`${j}-M`} className="w-[26px]" />,
                <col key={`${j}-S`} className="w-[26px]" />,
              ])}
              <col className="w-[50px] lg:w-[40px]" />
              <col className="w-[90px] lg:w-[80px]" />
            </colgroup>
            <thead>
              <tr>
                <th rowSpan={2} className="text-left px-2 py-2 sticky-col z-10 bg-ocean-50">Personne</th>
                {jours.map(j => {
                  const d = new Date(j)
                  return (
                    <th key={j} colSpan={2} className="px-0.5 py-1 text-center font-semibold text-ocean-700 border-l border-ocean-100/60 bg-ocean-50">
                      <div className="text-[10px] leading-tight whitespace-nowrap">{JOURS[d.getDay()]} {d.getDate()}/{String(d.getMonth() + 1).padStart(2, '0')}</div>
                    </th>
                  )
                })}
                <th rowSpan={2} className="px-1 py-2 text-right bg-ocean-50">Nb</th>
                <th rowSpan={2} className="px-1 py-2 text-right bg-ocean-50">Dû</th>
              </tr>
              <tr>
                {jours.flatMap(j => [
                  <th key={`${j}-M`} className="px-0 py-0.5 text-center text-[9px] text-ocean-600 border-l border-ocean-100/60 bg-ocean-50">M</th>,
                  <th key={`${j}-S`} className="px-0 py-0.5 text-center text-[9px] text-ocean-600 bg-ocean-50">S</th>,
                ])}
              </tr>
            </thead>
          </table>
        </div>

        {/* Corps : seul élément avec scroll horizontal actif.
            pt-1 = micro-buffer pour que la première ligne ne « colle » pas au header sticky. */}
        <div ref={bodyScrollRef} className="overflow-x-auto lg:overflow-x-visible rounded-b-2xl pt-1">
          <table className="text-xs sm:text-sm w-max lg:w-full lg:table-fixed">
            <colgroup>
              <col className="w-[170px]" />
              {jours.flatMap(j => [
                <col key={`${j}-M`} className="w-[26px]" />,
                <col key={`${j}-S`} className="w-[26px]" />,
              ])}
              <col className="w-[50px] lg:w-[40px]" />
              <col className="w-[90px] lg:w-[80px]" />
            </colgroup>
            <tbody>
              {lignesParCompte.map(([cid, fam]) => {
                const tot = totalParCompte.get(cid)
                return (
                  <FamilleBlock
                    key={cid}
                    compte_id={cid}
                    nom={fam.compte_nom}
                    personnes={fam.personnes}
                    jours={jours}
                    repasParJour={repasParJour}
                    presenceMap={presenceMap}
                    montantParPers={montantParPers}
                    nbParPers={nbParPers}
                    totalFamille={tot ? { nb_repas: tot.nb_repas_total, montant: tot.montant_du } : null}
                    nbColonnes={jours.length * 2}
                    collapsed={collapsed.has(cid)}
                    onToggleFamille={() => toggleFamille(cid)}
                    onToggle={(personne_id, cible_id, present) => mut.mutate({ personne_id, cible_id, present })}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-ocean-50 rounded-lg px-2.5 py-1">
      <div className="text-[9px] uppercase tracking-wide text-ocean-700 font-semibold whitespace-nowrap">{label}</div>
      <div className="font-bold text-ocean-900 leading-tight">{value}</div>
    </div>
  )
}

function FamilleBlock({
  compte_id, nom, personnes, jours, repasParJour, presenceMap,
  montantParPers, nbParPers, totalFamille, nbColonnes,
  collapsed, onToggleFamille, onToggle,
}: {
  compte_id: number
  nom: string
  personnes: PersonneT[]
  jours: string[]
  repasParJour: Record<string, { M?: number; S?: number }>
  presenceMap: Map<string, boolean>
  montantParPers: Map<number, number>
  nbParPers: Map<number, number>
  totalFamille: { nb_repas: number; montant: number } | null
  nbColonnes: number
  collapsed: boolean
  onToggleFamille: () => void
  onToggle: (personne_id: number, cible_id: number, present: boolean) => void
}) {
  return (
    <>
      <tr className="bg-gradient-sand">
        <td colSpan={nbColonnes + 3} className="px-0 py-0 sticky-col">
          <button
            type="button"
            onClick={onToggleFamille}
            className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-sand-200/50 transition text-left"
            aria-expanded={!collapsed}
            aria-controls={`fam-repas-${compte_id}`}
          >
            <span className="text-sand-800 text-xs">{collapsed ? '▸' : '▾'}</span>
            <span className="font-bold text-sand-800">👨‍👩‍👧 {nom}</span>
            {totalFamille && totalFamille.nb_repas > 0 && (
              <span className="ml-auto text-[11px] text-sand-700 font-semibold">
                {totalFamille.nb_repas} repas · {fmtEur(totalFamille.montant)}
              </span>
            )}
          </button>
        </td>
      </tr>
      {!collapsed && personnes.map(p => {
        const isBebe = p.categorie === 'bebe'
        return (
          <tr key={p.id} id={`fam-repas-${compte_id}`}>
            <td className="px-2 py-0.5 sticky-col">
              <div className="flex items-center gap-1.5">
                <Avatar personne_id={p.id} prenom={p.prenom} avatar_version={p.avatar_version} size="xs" />
                <span className={`font-medium truncate text-[12px] sm:text-sm ${isBebe ? 'text-ocean-500 italic' : 'text-ocean-900'}`}>{p.prenom}</span>
                {p.categorie !== 'adulte' && (
                  <span className={`tag-${p.categorie} hidden lg:inline-flex shrink-0`}>
                    {p.categorie === 'bebe' ? 'B' : p.categorie === 'enfant' ? 'E' : 'A'}
                  </span>
                )}
              </div>
            </td>
            {jours.flatMap(j => (['M', 'S'] as const).map(t => {
              const repasId = repasParJour[j][t]
              const key = repasId ? `${p.id}-${repasId}` : ''
              const on = presenceMap.get(key) ?? false
              const disabled = isBebe || !repasId
              return (
                <td key={`${j}-${t}`} className={`p-0 text-center ${t === 'M' ? 'border-l border-ocean-100/60' : ''}`}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => repasId && onToggle(p.id, repasId, !on)}
                    className={`cell cell-compact ${on ? 'cell-on' : 'cell-off'} ${disabled ? 'cell-disabled' : ''}`}
                  >
                    {on ? '✓' : ''}
                  </button>
                </td>
              )
            }))}
            <td className="px-1 py-0.5 text-right font-semibold text-ocean-900">{nbParPers.get(p.id) ?? 0}</td>
            <td className="px-1 py-0.5 text-right font-bold text-ocean-900">
              {fmtEur(montantParPers.get(p.id) ?? 0)}
            </td>
          </tr>
        )
      })}
      {!collapsed && totalFamille && (totalFamille.nb_repas > 0 || totalFamille.montant > 0) && (
        <tr className="bg-coral-50/60 border-t-2 border-coral-200">
          <td className="px-2 py-1 sticky-col bg-coral-50/60 text-right text-[10px] uppercase tracking-wide font-bold text-coral-700">
            Total {nom}
          </td>
          <td colSpan={jours.length * 2} className="text-right pr-1 text-[10px] text-coral-700 font-semibold">
            cumul →
          </td>
          <td className="px-1 py-1 text-right font-bold text-coral-700">{totalFamille.nb_repas}</td>
          <td className="px-1 py-1 text-right font-bold text-coral-700">{fmtEur(totalFamille.montant)}</td>
        </tr>
      )}
    </>
  )
}
